// src/features/authSlice.js
// FTR-0019: el login recibe un JWT (token + tokenExpiresAt) que se guarda en localStorage
// (src/services/authToken.js); /me y el resto de llamadas lo envían como Authorization: Bearer.
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import httpService from "../services/httpService.js";
import { clearAuthToken, getAuthToken, setAuthToken } from "../services/authToken.js";

export const SESSION_EXPIRED_MESSAGE = "Tu sesión expiró. Vuelve a iniciar sesión.";

const initialState = {
  user: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

export const LoginUser = createAsyncThunk(
  "user/LoginUser",
  async (user, thunkAPI) => {
    try {
      const response = await httpService.post("/login", {
        username: user.username,
        password: user.password,
      });

      const { token, tokenExpiresAt, ...authenticatedUser } = response.data || {};
      if (typeof token !== "string" || token === "") {
        clearAuthToken();
        return thunkAPI.rejectWithValue("El servidor no entregó un token de sesión");
      }
      setAuthToken(token, tokenExpiresAt);
      return authenticatedUser;
    } catch (error) {
      if (error.response) {
        const message = error.response.data?.msg || "Error al iniciar sesión";
        return thunkAPI.rejectWithValue(message);
      }

      return thunkAPI.rejectWithValue("No se pudo conectar con el servidor");
    }
  }
);

export const getMe = createAsyncThunk("user/getMe", async (_, thunkAPI) => {
  // Sin token no hay sesión posible: se evita el 401 de arranque y la página redirige al login.
  if (!getAuthToken()) return thunkAPI.rejectWithValue("Sesión no iniciada");
  try {
    const response = await httpService.get("/me");
    return response.data;
  } catch (error) {
    if (error.response) {
      const message =
        error.response.data?.msg || "No se pudo validar la sesión";
      return thunkAPI.rejectWithValue(message);
    }

    return thunkAPI.rejectWithValue("No se pudo conectar con el servidor");
  }
});

export const LogOut = createAsyncThunk("user/LogOut", async (_, thunkAPI) => {
  // El token se borra localmente ANTES de llamar al backend (la pestaña queda cerrada aunque la red
  // falle) y se envía explícitamente para que el backend revoque su fila de sesión.
  const token = getAuthToken();
  clearAuthToken();
  if (!token) return;
  try {
    await httpService.delete("/logout", { headers: { Authorization: `Bearer ${token}` } });
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.msg || "No se pudo cerrar sesión";
      return thunkAPI.rejectWithValue(message);
    }

    return thunkAPI.rejectWithValue("No se pudo conectar con el servidor");
  }
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    reset: () => initialState,
    // Token expirado/revocado (401 de la API) o logout en otra pestaña: sin usuario y con aviso.
    sessionExpired: (_state, action) => ({
      ...initialState,
      isError: true,
      message: typeof action?.payload === "string" && action.payload ? action.payload : SESSION_EXPIRED_MESSAGE,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(LoginUser.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
      state.isSuccess = false;
      state.message = "";
    });

    builder.addCase(LoginUser.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
      state.user = action.payload;
      state.message = "";
    });

    builder.addCase(LoginUser.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.isSuccess = false;
      state.message = action.payload;
    });

    // Get User Login
    builder.addCase(getMe.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
      state.message = "";
    });

    builder.addCase(getMe.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
      state.user = action.payload;
      state.message = "";
    });

    builder.addCase(getMe.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.isSuccess = false;
      state.message = action.payload;
    });

    builder.addCase(LogOut.pending, (state) => {
      state.isLoading = true;
      state.message = "";
    });

    builder.addCase(LogOut.fulfilled, (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.user = null;
      state.message = "";
    });

    builder.addCase(LogOut.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      // El token ya fue descartado localmente: la sesión de esta pestaña termina aunque el backend falle.
      state.user = null;
      state.message = action.payload;
    });
  },
});

export const { reset, sessionExpired } = authSlice.actions;
export default authSlice.reducer;